import type { JackpotTask } from '@/features/jackpot/jackpot-api';

export const JACKPOT_TASK_ORDER = ['BET', 'DEPOSIT', 'VIP_LOGIN'] as const;
/** 墨西哥字母奖池按站点时区日切，与 `ActivityTime.zoneOf` 一致。 */
export const JACKPOT_RESET_TIME_ZONE = 'America/Mexico_City';

export function sortJackpotTasks<T extends { condition: string }>(tasks: T[]): T[] {
  return [...tasks].sort((left, right) => {
    const leftIndex = JACKPOT_TASK_ORDER.indexOf(left.condition as (typeof JACKPOT_TASK_ORDER)[number]);
    const rightIndex = JACKPOT_TASK_ORDER.indexOf(right.condition as (typeof JACKPOT_TASK_ORDER)[number]);
    return (leftIndex < 0 ? 99 : leftIndex) - (rightIndex < 0 ? 99 : rightIndex);
  });
}

/** 源站 countdown：站点时区到次日 00:00 的剩余秒。加载失败时前端自己算，避免冻在 00:00:00。 */
export function secondsUntilJackpotReset(
  nowMs = Date.now(),
  timeZone = JACKPOT_RESET_TIME_ZONE,
): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(nowMs));
  const read = (type: Intl.DateTimeFormatPartTypes) => {
    const value = parts.find((part) => part.type === type)?.value;
    return Number(value);
  };
  const hourRaw = read('hour');
  const hour = hourRaw === 24 ? 0 : hourRaw;
  const minute = read('minute');
  const second = read('second');
  if (![hour, minute, second].every((value) => Number.isFinite(value))) return 0;
  return Math.max(0, 86_400 - (hour * 3600 + minute * 60 + second));
}

/** 源站 Hora de reinicio：剩余秒数格式化成 HH:MM:SS。 */
export function formatJackpotCountdown(sec: number): string {
  const total = Number.isFinite(sec) ? Math.max(0, Math.floor(sec)) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** 奖池中奖条：当前条 + 下一条，供向上轮播。不足 2 条时不滚。 */
export function jackpotTickerPair<T>(items: T[], index: number): { current: T | null; next: T | null } {
  if (!items.length) return { current: null, next: null };
  const safeIndex = ((index % items.length) + items.length) % items.length;
  const current = items[safeIndex] ?? null;
  if (items.length < 2 || current === null) return { current, next: null };
  return { current, next: items[(safeIndex + 1) % items.length] ?? null };
}

export function jackpotTaskProgressPct(task: Pick<JackpotTask, 'granted' | 'progressMinor' | 'thresholdMinor'>): number {
  if (task.granted) return 100;
  if (task.thresholdMinor <= 0) return 0;
  return Math.min(100, Math.max(0, (task.progressMinor / task.thresholdMinor) * 100));
}
